import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell } from "recharts";
import { ListBullets, Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { inr } from "../components/bom/CostSummary";
import { useTheme } from "../context/ThemeContext";
import { tc } from "../lib/theme";

const PR_COLORS = { draft: "var(--c-muted)", inquiry_created: "var(--c-accent)", quoted: "var(--c-green)", ordered: "var(--c-amber)", received: "var(--c-green)", closed: "var(--c-green)", cancelled: "var(--c-red)" };

export default function BomDashboard() {
  const [data, setData] = useState(null);
  const [industry, setIndustry] = useState("");
  const nav = useNavigate();
  useTheme();
  const pal = [tc("accent"), tc("green"), tc("amber"), tc("red"), "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

  useEffect(() => {
    api.get("/bom-dashboard").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const projects = useMemo(() => {
    if (!data) return [];
    return data.projects.filter((p) => !industry || p.industry === industry);
  }, [data, industry]);

  const withBom = projects.filter((p) => p.summary);
  const costNorm = withBom.filter((p) => p.cost_per_kva);
  const avgNorm = costNorm.length ? costNorm.reduce((s, p) => s + p.cost_per_kva, 0) / costNorm.length : 0;

  const catAgg = useMemo(() => {
    const agg = {};
    for (const p of withBom) {
      for (const [cid, c] of Object.entries(p.categories || {})) {
        agg[cid] = (agg[cid] || 0) + (c.cost || 0);
      }
    }
    const total = Object.values(agg).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(agg).map(([k, v], i) => ({
      name: k.replace(/_/g, " "), value: Math.round(1000 * v / total) / 10, color: pal[i % pal.length],
    })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [withBom]);

  const industries = data ? [...new Set(data.projects.map((p) => p.industry))] : [];
  const allPrs = (data?.prs || []).filter((p) => !industry || projects.some((pr) => pr.id === p.project_id))
    .sort((a, b) => (a.required_by_date || "").localeCompare(b.required_by_date || ""));
  const today = new Date().toISOString().slice(0, 10);
  const projName = (pid) => data?.projects.find((p) => p.id === pid)?.code || pid.slice(0, 8);

  return (
    <div className="space-y-4" data-testid="bom-dashboard">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg text-[var(--c-text)] font-medium flex items-center gap-2"><ListBullets size={20} className="text-[var(--c-accent)]" /> BOM & Procurement Dashboard</h1>
        <select data-testid="dash-industry-filter" value={industry} onChange={(e) => setIndustry(e.target.value)}
          className="ml-auto h-8 bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
          <option value="">All industries</option>
          {industries.map((i) => <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {/* Section 1 — projects summary */}
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto" data-testid="dash-projects">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
              <th className="py-2 px-3">Project</th><th className="py-2 px-3">Client</th><th className="py-2 px-3">Industry</th>
              <th className="py-2 px-3 text-right">Items</th><th className="py-2 px-3 text-right">Weight</th>
              <th className="py-2 px-3 text-right">Est Cost</th><th className="py-2 px-3 text-right">Costed %</th>
              <th className="py-2 px-3 text-right">PRs Open</th><th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} className="border-b border-[var(--c-border)]/40 hover:bg-[var(--c-border)]/25 cursor-pointer"
                onClick={() => nav(`/projects/${p.id}?tab=bom`)} data-testid={`dash-row-${p.code}`}>
                <td className="py-2 px-3 font-mono text-[11px] text-[var(--c-accent)]">{p.code}</td>
                <td className="py-2 px-3 text-[11px] text-[var(--c-text)]">{p.client}</td>
                <td className="py-2 px-3 text-[10px] text-[var(--c-muted)]">{p.industry.replace(/_/g, " ")}</td>
                <td className="py-2 px-3 text-right font-mono text-[11px]">{p.summary?.item_count ?? "—"}</td>
                <td className="py-2 px-3 text-right font-mono text-[11px]">{p.summary ? `${Number(p.summary.total_weight).toLocaleString()} kg` : "—"}</td>
                <td className="py-2 px-3 text-right font-mono text-[11px] text-[var(--c-text)]">{p.summary ? inr(p.summary.total_cost) : "—"}</td>
                <td className="py-2 px-3 text-right font-mono text-[11px]"
                  style={{ color: p.costed_pct > 90 ? "var(--c-green)" : p.costed_pct >= 60 ? "var(--c-amber)" : "var(--c-red)" }}>
                  {p.summary ? `${p.costed_pct}%` : "—"}
                </td>
                <td className="py-2 px-3 text-right font-mono text-[11px]">{p.prs_open}</td>
                <td className="py-2 px-3 text-[10px]">{p.summary ? <span className="text-[var(--c-green)]">✅ Active</span> : <span className="text-[var(--c-muted)]">⏳ No BOM</span>}</td>
              </tr>
            ))}
            {!projects.length && <tr><td colSpan={9} className="py-8 text-center text-[11px] text-[var(--c-muted)]">No projects.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Section 2 — cost per kVA comparison */}
        {costNorm.length >= 1 && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="dash-cost-per-kva">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Cost per kVA comparison</div>
            <div className="h-48">
              <ResponsiveContainer>
                <BarChart data={costNorm.map((p) => ({ name: p.code, v: p.cost_per_kva }))}>
                  <XAxis dataKey="name" tick={{ fill: tc("muted"), fontSize: 9 }} />
                  <YAxis tick={{ fill: tc("muted"), fontSize: 9 }} />
                  <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}/kVA`, "Cost/kVA"]}
                    contentStyle={{ background: "var(--c-surface)", border: "1px solid var(--c-border)", fontSize: 11 }} />
                  <Bar dataKey="v" fill={tc("accent")} radius={[2, 2, 0, 0]} />
                  {avgNorm > 0 && <ReferenceLine y={avgNorm} stroke={tc("amber")} strokeDasharray="5 4" label={{ value: `avg ₹${Math.round(avgNorm)}`, fill: tc("amber"), fontSize: 9 }} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Section 3 — category breakdown */}
        {catAgg.length > 0 && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 flex items-center gap-4" data-testid="dash-cat-breakdown">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] absolute">Category cost share</div>
            <div className="w-52 h-48 shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={catAgg} dataKey="value" nameKey="name" innerRadius={30} outerRadius={60} strokeWidth={1} stroke={tc("bg")}>
                    {catAgg.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: "var(--c-surface)", border: "1px solid var(--c-border)", fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1">
              {catAgg.map((d) => (
                <div key={d.name} className="flex items-center gap-1.5 text-[10px]">
                  <span className="w-2 h-2 rounded-[2px]" style={{ background: d.color }} />
                  <span className="text-[var(--c-muted)] capitalize">{d.name}</span>
                  <span className="ml-auto font-mono text-[var(--c-text)]">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section 4 — PR overview */}
      {data && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid="dash-pr-overview">
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <span className="text-[11px] text-[var(--c-text)]">Purchase Requisitions — all projects: <b>{data.pr_summary.total}</b></span>
            {[["draft", "Draft"], ["inquiry_created", "Inquiry sent"], ["quoted", "Quoted"], ["ordered", "Ordered"], ["received", "Received"]].map(([k, l]) => (
              <span key={k} className="text-[10px] font-mono" style={{ color: PR_COLORS[k] }}>{l}: {data.pr_summary[k] || 0}</span>
            ))}
            {data.overdue_count > 0 && (
              <span className="text-[10px] text-[var(--c-red)] flex items-center gap-1"><Warning size={11} /> {data.overdue_count} overdue</span>
            )}
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-[var(--c-surface)]">
                <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
                  <th className="py-2 px-2">PR ID</th><th className="py-2 px-2">Project</th><th className="py-2 px-2">Item</th>
                  <th className="py-2 px-2">Required By</th><th className="py-2 px-2">Vendor</th><th className="py-2 px-2 text-right">Est Cost</th>
                  <th className="py-2 px-2">Status</th><th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {allPrs.map((pr) => {
                  const overdue = pr.required_by_date && pr.required_by_date < today && !["received", "closed", "cancelled"].includes(pr.status);
                  return (
                    <tr key={pr.pr_id} className="border-b border-[var(--c-border)]/40 hover:bg-[var(--c-border)]/25 cursor-pointer"
                      onClick={() => nav(`/projects/${pr.project_id}?tab=bom`)} data-testid={`dash-pr-${pr.pr_id}`}>
                      <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-accent)]">{pr.pr_id}</td>
                      <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)]">{projName(pr.project_id)}</td>
                      <td className="py-1.5 px-2 text-[11px] text-[var(--c-text)]">{pr.description}</td>
                      <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)]">{pr.required_by_date}</td>
                      <td className="py-1.5 px-2 text-[10px] text-[var(--c-muted)]">{pr.suggested_vendor || "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-[10px]">{inr(pr.estimated_cost)}</td>
                      <td className="py-1.5 px-2 text-[10px]" style={{ color: PR_COLORS[pr.status] }}>{pr.status.replace(/_/g, " ")}</td>
                      <td className="py-1.5 px-2 text-[10px]">{overdue ? "🔴" : ""}</td>
                    </tr>
                  );
                })}
                {!allPrs.length && <tr><td colSpan={8} className="py-8 text-center text-[11px] text-[var(--c-muted)]">No purchase requisitions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
